import * as React from 'react'
import {
  TransitionProps,
  getTransitionProps,
  getAutoHeightDuration,
} from '../transitions'
import { SxProps, sx as sxFunc } from '../sx'
import { Transition } from 'react-transition-group'
import { useForkRef } from '../../hooks'
import clsx from 'clsx'
import styles from './index.module.scss'

export interface CollapseClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the root element if `orientation="horizontal"`. */
  horizontal: string
  /** Styles applied to the root element when the transition has entered. */
  entered: string
  /** Styles applied to the root element when the transition has exited and `collapsedSize` = 0px. */
  hidden: string
  /** Styles applied to the outer wrapper element. */
  wrapper: string
  /** Styles applied to the inner wrapper element. */
  wrapperInner: string
}

export interface CollapseProps extends Omit<TransitionProps, 'timeout'> {
  /**
   * The content node to be collapsed.
   */
  children?: React.ReactNode
  className?: string
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<CollapseClasses>
  /**
   * The width (horizontal) or height (vertical) of the container when collapsed.
   * @default '0px'
   */
  collapsedSize?: string | number
  /**
   * The transition timing function.
   * You may specify a single easing or a object containing enter and exit values.
   */
  easing?: TransitionProps['easing']
  /**
   * If `true`, the component will transition in.
   */
  in?: boolean
  /**
   * The transition orientation.
   * @default 'vertical'
   */
  orientation?: 'horizontal' | 'vertical'
  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   *
   * Set to 'auto' to automatically calculate transition time based on height.
   * @default duration.standard
   */
  timeout?: TransitionProps['timeout'] | 'auto'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

/**
 * The Collapse transition is used by the
 * [Vertical Stepper](/material-ui/react-stepper/#vertical-stepper) StepContent component.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
export const Collapse = React.forwardRef<HTMLDivElement, CollapseProps>(
  function Collapse(props, ref) {
    const {
      addEndListener,
      children,
      className,
      classes,
      collapsedSize: collapsedSizeProp = '0px',
      easing,
      in: inProp,
      onEnter,
      onEntered,
      onEntering,
      onExit,
      onExited,
      onExiting,
      orientation = 'vertical',
      style,
      timeout = 300, // standard
      sx,
      ...other
    } = props

    const timer = React.useRef<NodeJS.Timeout>()
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const autoTransitionDuration = React.useRef<number>()
    const collapsedSize =
      typeof collapsedSizeProp === 'number'
        ? `${collapsedSizeProp}px`
        : collapsedSizeProp
    const isHorizontal = orientation === 'horizontal'
    const size = isHorizontal ? 'width' : 'height'
    const nodeRef = React.useRef<HTMLDivElement>(null)
    const handleRef = useForkRef(ref, nodeRef)

    React.useEffect(() => {
      return () => {
        // @ts-ignore
        clearTimeout(timer.current)
      }
    }, [])

    const normalizedTransitionCallback =
      (callback: (node: HTMLElement, isAppearing?: boolean) => void) =>
      (maybeIsAppearing: boolean) => {
        if (callback) {
          const node = nodeRef.current!

          // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
          if (maybeIsAppearing === undefined) {
            callback(node)
          } else {
            callback(node, maybeIsAppearing)
          }
        }
      }

    const getWrapperSize = () =>
      wrapperRef.current
        ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight']
        : 0

    const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
      if (wrapperRef.current && isHorizontal) {
        // Set absolute position to get the size of collapsed content
        wrapperRef.current.style.position = 'absolute'
      }
      node.style[size] = collapsedSize

      if (onEnter) {
        onEnter(node, isAppearing!)
      }
    })

    const handleEntering = normalizedTransitionCallback((node, isAppearing) => {
      const wrapperSize = getWrapperSize()

      if (wrapperRef.current && isHorizontal) {
        // After the size is read reset the position back to default
        wrapperRef.current.style.position = ''
      }

      const { duration: transitionDuration, easing: transitionTimingFunction } =
        getTransitionProps(
          //@ts-ignore
          { style, timeout, easing },
          {
            mode: 'enter',
          }
        )

      if (timeout === 'auto') {
        const duration2 = getAutoHeightDuration(wrapperSize)
        node.style.transitionDuration = `${duration2}ms`
        autoTransitionDuration.current = duration2
      } else {
        node.style.transitionDuration =
          typeof transitionDuration === 'string'
            ? transitionDuration
            : `${transitionDuration}ms`
      }

      node.style[size] = `${wrapperSize}px`
      node.style.transitionTimingFunction = transitionTimingFunction!

      if (onEntering) {
        onEntering(node, isAppearing!)
      }
    })

    const handleEntered = normalizedTransitionCallback((node, isAppearing) => {
      node.style[size] = 'auto'

      if (onEntered) {
        onEntered(node, isAppearing!)
      }
    })

    const handleExit = normalizedTransitionCallback(node => {
      node.style[size] = `${getWrapperSize()}px`

      if (onExit) {
        onExit(node)
      }
    })

    const handleExited = normalizedTransitionCallback(onExited as any)

    const handleExiting = normalizedTransitionCallback(node => {
      const wrapperSize = getWrapperSize()
      const { duration: transitionDuration, easing: transitionTimingFunction } =
        getTransitionProps(
          //@ts-ignore
          { style, timeout, easing },
          {
            mode: 'exit',
          }
        )

      if (timeout === 'auto') {
        // Actually it just calculates animation duration based on size
        const duration2 = getAutoHeightDuration(wrapperSize)
        node.style.transitionDuration = `${duration2}ms`
        autoTransitionDuration.current = duration2
      } else {
        node.style.transitionDuration =
          typeof transitionDuration === 'string'
            ? transitionDuration
            : `${transitionDuration}ms`
      }

      node.style[size] = collapsedSize
      node.style.transitionTimingFunction = transitionTimingFunction!

      if (onExiting) {
        onExiting(node)
      }
    })

    const handleAddEndListener = (next: any) => {
      if (timeout === 'auto') {
        timer.current = setTimeout(next, autoTransitionDuration.current || 0)
      }
      if (addEndListener) {
        // Old call signature before `react-transition-group` implemented `nodeRef`
        addEndListener(nodeRef.current!, next)
      }
    }

    return (
      // @ts-ignore
      <Transition
        in={inProp}
        onEnter={handleEnter}
        onEntered={handleEntered}
        onEntering={handleEntering}
        onExit={handleExit}
        onExited={handleExited}
        onExiting={handleExiting}
        addEndListener={handleAddEndListener}
        nodeRef={nodeRef}
        timeout={timeout === 'auto' ? null : timeout}
        {...other}
      >
        {(state: any, childProps: any) => {
          return (
            <div
              className={clsx(
                styles.root,
                classes?.root,
                state === 'entered' && [styles.entered, classes?.entered],
                state === 'exited' &&
                  !inProp &&
                  collapsedSize === '0px' && [styles.exited, classes?.hidden],

                className,
                sxFunc(sx)
              )}
              style={{
                [isHorizontal ? 'minWidth' : 'minHeight']: collapsedSize,
                ...style,
              }}
              ref={handleRef}
              {...childProps}
            >
              <div
                className={clsx(styles.wrapper, classes?.wrapper)}
                ref={wrapperRef}
              >
                <div
                  className={clsx(styles.wrapperInner, classes?.wrapperInner)}
                >
                  {children}
                </div>
              </div>
            </div>
          )
        }}
      </Transition>
    )
  }
)

// @ts-ignore
Collapse.factorSupportAuto = true
